<template>
	<div class="modal-wrapper">
		<div class="modal-container">
			<div class="modal">
				<div class="title-container">
					<div class="title">Advanced Filters</div>
					<div class="buttons">
						<a href @click.prevent="$emit('cancel')" class="button"><div class="hover-title">Cancel</div><span class="fa fa-times"></span></a>
					</div>
				</div>
				<div v-if="loading" class="loader inline"><img src="/images/spinner.svg" class="fa fa-spin spinner" aria-hidden="true"></div>
				<form v-if="!loading" method="post" @submit.prevent="submit">
					<div class="grid form two">
						<div class="input-container">
							<div class="input-title required">Field</div>
							<div class="input-box">
								<selectize v-model="form.field" :settings="{ create: false }">
									<option :value="null" disabled readonly>Choose Field</option>
									<option v-for="field in fields" :key="`filter-field-${module.id}-${field.slug}`" :value="field.slug">{{field.name }}</option>
								</selectize>
							</div>
						</div>
						<div class="input-container">
							<div class="input-title required">Where</div>
							<div class="input-box">
								<selectize v-model="form.operand" :settings="{ create: false }" :disabled="form.field === null">
									<option v-for="operand in operands" :key="`filter-operand-${module.id}-${filterField.slug}-${operand.value}`" :value="operand.value">{{ operand.label }}</option>
								</selectize>
							</div>
						</div>
					</div>
					<div v-if="canHaveValue">
						<div v-if="filterField && filterField.type && valueType === VALUE_TYPE.BASIC" class="input-container">
							<div class="input-title required">Value</div>
							<div class="input-box"><input type="text" v-model="form.value"></div>
						</div>
						<div v-if="valueType === VALUE_TYPE.DATE" class="input-container">
							<div class="input-title">{{ form.operand === 'between' ? 'Date Range' : 'Date' }}</div>
							<div class="input-box"><date-picker name="dashboard-range" v-model="form.value" @input="onDateSelected" :range="form.operand === 'between'"></date-picker></div>
						</div>
						<div v-if="filterField && valueType === VALUE_TYPE.TAGS" class="input-container">
							<div class="input-title required">Value</div>
							<div class="input-box">
								<selectize v-model="form.value" multiple :settings="{ create: false }">
									<option v-for="tag in relations.tags['v_' + filterField.id]" :key="`filter-tag-operand-${module.id}-${filterField.slug}-${tag.name}`" :value="tag.id">{{ tag.name }}</option>
								</selectize>
							</div>
						</div>
						<div v-if="filterField && valueType === VALUE_TYPE.ASSOCIATED" class="input-container">
							<div class="input-title required">Value</div>
							<div class="input-box">
								<selectize v-model="form.value" multiple :settings="{ create: false }">
									<option v-for="[id, value] in Object.entries((relations.associatedModules || {})['v_' + filterField.id])" :key="`filter-associated-operand-${module.id}-${filterField.slug}-${id}`" :value="id">{{ value }}</option>
								</selectize>
							</div>
						</div>
						<div v-if="filterField && valueType === VALUE_TYPE.SUPER_DEFINED" class="input-container">
							<div class="input-title required">Value</div>
							<div class="input-box">
								<selectize v-model="form.value" :settings="{ create: false }">
									<option v-for="value in (filterField.specific_data || '').split(',').map(s => s.trim())" :key="`filter-super_defined-operand-${module.id}-${filterField.slug}-${value}`" :value="value">{{ value }}</option>
								</selectize>
							</div>
						</div>
					</div>
					<button type="submit" class="good-bg">Add Filter<span class="fa fa-plus"></span></button>
				</form>
			</div>
		</div>
	</div>
</template>

<script>

import _ from 'underscore';
import { VALUE_TYPE } from './filter-description';

/**
 * DISABLED:
	photo_gallery
	file_gallery
	photo
	file
	current_gps
BASIC LIKE %search_text%:
	wysiwyg
	static
	numeric
 	string
MUST QUERY BACKEND FOR LIST OF OPTIONS
	tags
	associated_module
CUSTOM OVERRIDE
	status
DATE - MUST SUPPORT: range, before, after, equal to, not equal to
	date
 */

export default {

	props: [ 'module', 'filter', 'loading', 'relations' ],

	data() {
		return {
			VALUE_TYPE,
			form: this.filter ? this.filter : {
				field: null,
				type: null,
				operand: 'LIKE',
				value: null,
				id: _.uniqueId(`${this.module.id}-list-filter`),
			},
		};
	},

	computed: {

		fieldsBySlug() {
			return _.indexBy(this.module.fields, 'slug');
		},

		fields() {
			const DISABLED_FIELD_TYPES = [
				'photo_gallery', 'file_gallery', 'photo', 'file', 'current_gps',
			];
			return this.module.fields.filter(field => !DISABLED_FIELD_TYPES.includes(field.type));
		},

		filterField() {
			return this.fieldsBySlug[this.form.field];
		},

		fieldFieldType() {
			if (this.filterField) {
				return this.filterField.type;
			}

			return null;
		},

		valueType() {
			if (!this.filterField) return VALUE_TYPE.BASIC;
			switch (this.filterField.type) {
				case 'status':
					return VALUE_TYPE.STATUS;
				case 'date':
					return VALUE_TYPE.DATE;
				case 'tags':
					return VALUE_TYPE.TAGS;
				case 'associated_module':
					return VALUE_TYPE.ASSOCIATED;
				case 'super_dropdown':
					return VALUE_TYPE.SUPER_DEFINED;
				case 'wysiwyg':
				case 'static':
				case 'numeric':
				case 'string':
				default:
					return VALUE_TYPE.BASIC;
			}
		},

		canHaveValue() {
			if (!this.form.operand) return false;
			return !['empty', 'not empty'].includes(this.form.operand);
		},

		operands() {
			if (!this.filterField) return [];

			switch (this.filterField.type) {
				case 'status':
					return [
						{ value: '=', label: 'is Active' },
						{ value: '!=', label: 'is Not Active' },
					];
				case 'date':
					return [
						{ value: '=', label: 'is equal to' },
						{ value: '!=', label: 'is not equal to' },
						{ value: '<', label: 'is before' },
						{ value: '<=', label: 'is on or before' },
						{ value: '>', label: 'is after' },
						{ value: '>=', label: 'is on or after' },
						{ value: 'between', label: 'is between' },
						{ value: 'empty', label: 'is empty' },
						{ value: 'not empty', label: 'is not empty' },
					];
				case 'tags':
				case 'associated_module':
					return [
						// TODO - How to query laravel for has ALL?
						{ value: 'all', label: 'has all' },
						{ value: 'any', label: 'has any' },
					];
				case 'super_dropdown':
					return [
						{ value: '=', label: 'is equal to' },
						{ value: '!=', label: 'is not equal to' },
						{ value: 'empty', label: 'is empty' },
						{ value: 'not empty', label: 'is not empty' },
					];
				case 'wysiwyg':
				case 'static':
				case 'numeric':
				case 'string':
				default:
					return [
						{ value: 'LIKE', label: 'contains' },
						{ value: 'NOT LIKE', label: 'does not contain' },
						{ value: '=', label: 'is equal to' },
						{ value: '!=', label: 'is not equal to' },
						{ value: 'empty', label: 'is empty' },
						{ value: 'not empty', label: 'is not empty' },
					];
			}
		}
	},

	watch: {
		fieldFieldType(newValue) {
			this.form.type = newValue;
			if (newValue === 'tags' || newValue === 'associated_module') {
				this.form.operand = 'any';
			}
			if (newValue === 'super_dropdown') {
				this.form.operand = '=';
			}
		},

		'form.field': function(newValue) {
			this.form.value = null;
		},

		'form.operand': function(newOperand, oldOperand) {
			if (oldOperand === 'between') {
				// Switching to operand
				this.form.value = [this.form.value, null];
			}
			if (this.valueType === VALUE_TYPE.DATE) {
				if (newOperand !== 'between' && Array.isArray(this.form.value)) {
					this.form.value = this.form.value[ 0 ];
				}
			}
		},
	},

	methods: {

		onDateSelected() {
			console.log('date selected', this.form);
		},

		submit() {
			this.$emit('submit', this.form);
		}

	},
};

</script>
